import { useState } from "react";
import { toast } from "sonner";
import { Palette, Building2, Megaphone, Check } from "lucide-react";
import { api } from "@/lib/api";

const TARGETS = [
  { key: "privati", Icon: Palette, t: "Privati", d: "Corsi di cucina d'arte, coaching 1:1, esperienze su misura e accesso prioritario agli Assaggi d'Arte." },
  { key: "horeca", Icon: Building2, t: "Ristoranti · Hotel · Agriturismi", d: "Un evento Assaggi d'Arte nel tuo locale, consulenza menu ispirata alle opere, formazione dello staff." },
  { key: "creator", Icon: Megaphone, t: "Creator & Aziende", d: "Formazione AI, contenuti e serie con Wilder, eventi brandizzati e partnership di lungo periodo." },
];

const STEP2 = {
  privati: ["Cosa vuoi imparare? (cucina, arte, AI…)", "Hai già seguito corsi online?"],
  horeca: ["Che tipo di struttura hai? Dove?", "Quanti coperti / camere?"],
  creator: ["Di cosa ti occupi? Che canali usi?", "Obiettivo: contenuti, formazione o partnership?"],
};

export default function Servizi() {
  const [step, setStep] = useState(0);
  const [target, setTarget] = useState(null);
  const [answers, setAnswers] = useState(["", ""]);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", company: "", city: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", {
        target,
        ...contact,
        message: answers.filter(Boolean).join(" | "),
        details: { q1: answers[0], q2: answers[1] },
      });
      setDone(true);
      toast.success("Richiesta inviata! Ti ricontattiamo entro 48 ore.");
    } catch {
      toast.error("Errore nell'invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="servizi-page">
      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-4">Servizi & Consulenze</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
            Portiamo l'arte<br />dove lavori e vivi
          </h1>
          <p className="text-sm text-[#C9C4B8] leading-relaxed max-w-2xl">
            Tre percorsi, un unico metodo: unire arte, cucina e AI per creare esperienze che restano.
            Compila il form qualificato: 2 minuti, poi ti chiamiamo noi.
          </p>
        </div>
      </section>

      <section className="border-b border-[#2A2A2A] bg-[#141414]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
          {TARGETS.map((s) => (
            <div key={s.key} className="bg-[#0A0A0A] p-8" data-testid={`service-detail-${s.key}`}>
              <s.Icon size={24} className="text-[#E4BD44] mb-5" />
              <h3 className="font-display text-xl font-bold mb-2">{s.t}</h3>
              <p className="text-sm text-[#C9C4B8] leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="max-w-2xl mx-auto px-5 lg:px-10 py-16">
          {done ? (
            <div className="text-center py-12 fade-up" data-testid="lead-success">
              <Check size={40} className="mx-auto text-[#E4BD44] mb-5" />
              <h2 className="font-display text-3xl font-bold mb-3">Richiesta ricevuta!</h2>
              <p className="text-sm text-[#C9C4B8]">Ti ricontatteremo entro 48 ore per fissare una call conoscitiva gratuita.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-8" data-testid="lead-form-steps">
                {[0, 1, 2].map((i) => (
                  <div key={i} className={`h-1 flex-1 ${i <= step ? "bg-[#E4BD44]" : "bg-[#2A2A2A]"} transition-colors`} />
                ))}
              </div>

              {step === 0 && (
                <div className="fade-up">
                  <h2 className="font-display text-lg font-bold mb-6">1 · Chi sei?</h2>
                  <div className="flex flex-col gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
                    {TARGETS.map((s) => (
                      <button key={s.key} onClick={() => { setTarget(s.key); setStep(1); }}
                        className="bg-[#0A0A0A] hover:bg-[#141414] transition-colors px-6 py-5 flex items-center gap-4 text-left"
                        data-testid={`lead-target-${s.key}`}>
                        <s.Icon size={20} className="text-[#E4BD44]" />
                        <span className="font-display text-lg font-bold">{s.t}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="fade-up">
                  <h2 className="font-display text-lg font-bold mb-6">2 · Raccontaci di più</h2>
                  <div className="space-y-4">
                    {STEP2[target].map((q, i) => (
                      <div key={q}>
                        <label className="text-sm font-medium block mb-1.5">{q}</label>
                        <textarea rows={2} className="ws-input" value={answers[i]}
                          onChange={(e) => setAnswers(answers.map((a, j) => (j === i ? e.target.value : a)))}
                          data-testid={`lead-q${i + 1}`} />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-8">
                    <button onClick={() => setStep(0)} className="btn-outline" data-testid="lead-back-1">Indietro</button>
                    <button onClick={() => setStep(2)} className="btn-primary" data-testid="lead-next-2">Avanti</button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <form onSubmit={submit} className="fade-up">
                  <h2 className="font-display text-lg font-bold mb-6">3 · I tuoi contatti</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input required className="ws-input" placeholder="Nome e cognome" value={contact.name}
                      onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="lead-name" />
                    <input required type="email" className="ws-input" placeholder="Email" value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="lead-email" />
                    <input className="ws-input" placeholder="Telefono" value={contact.phone}
                      onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="lead-phone" />
                    <input className="ws-input" placeholder={target === "privati" ? "Città" : "Nome attività / azienda"}
                      value={target === "privati" ? contact.city : contact.company}
                      onChange={(e) => setContact(target === "privati" ? { ...contact, city: e.target.value } : { ...contact, company: e.target.value })}
                      data-testid="lead-company" />
                  </div>
                  <div className="flex justify-between mt-8">
                    <button type="button" onClick={() => setStep(1)} className="btn-outline" data-testid="lead-back-2">Indietro</button>
                    <button type="submit" disabled={loading} className="btn-primary" data-testid="lead-submit">
                      {loading ? "Invio…" : "Invia richiesta"}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
